/**
 * Judge.me → custom review UI (PDP preview grid + Read-all dialog + landing marquee).
 */
(function () {
  var widgetPool = null;
  var dialogWired = false;
  var reviewControllers = [];

  var LANDING_FEATURED_AUTHORS = ['anju mahendru', 'dr. sharlene bengers', 'dinesh dayal'];

  function isFeaturedAuthor(authorName) {
    var author = (authorName || '').toLowerCase().trim();
    if (!author) return false;
    return LANDING_FEATURED_AUTHORS.some(function (name) {
      return author === name || author.indexOf(name) !== -1;
    });
  }

  function reviewsForMarquee(reviews, root) {
    if (!reviews || !reviews.length) return [];
    if (root && root.getAttribute('data-exclude-featured-marquee') === 'true') {
      return reviews.filter(function (r) {
        return !isFeaturedAuthor(r.author);
      });
    }
    return reviews;
  }

  function esc(text) {
    var el = document.createElement('span');
    el.textContent = text || '';
    return el.innerHTML;
  }

  function starsHtml(score) {
    var n = Math.max(0, Math.min(5, Math.round(Number(score) || 5)));
    var filled = '★★★★★'.slice(0, n);
    if (n >= 5) return filled;
    return filled + '<span class="star-off">' + '★★★★★'.slice(0, 5 - n) + '</span>';
  }

  function formatHelpful(raw) {
    var t = (raw || '').trim();
    if (!t) return '';
    if (/👍|helpful/i.test(t)) return t;
    var num = t.match(/(\d+)/);
    return num ? '👍 ' + num[1] : t;
  }

  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatReviewDate(raw) {
    if (!raw) return '';
    var s = String(raw).trim();
    if (!s) return '';

    var alreadyNamed = s.match(/(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})/);
    if (alreadyNamed) {
      var mon = alreadyNamed[2].slice(0, 3);
      mon = mon.charAt(0).toUpperCase() + mon.slice(1).toLowerCase();
      return alreadyNamed[1] + ' ' + mon + ' ' + alreadyNamed[3];
    }

    var slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) {
      var p1 = parseInt(slash[1], 10);
      var p2 = parseInt(slash[2], 10);
      var year = parseInt(slash[3], 10);
      var month;
      var day;
      if (p1 > 12) {
        day = p1;
        month = p2;
      } else if (p2 > 12) {
        month = p1;
        day = p2;
      } else {
        month = p1;
        day = p2;
      }
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return day + ' ' + MONTHS_SHORT[month - 1] + ' ' + year;
      }
    }

    var iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      var m = parseInt(iso[2], 10);
      var d = parseInt(iso[3], 10);
      var y = parseInt(iso[1], 10);
      if (m >= 1 && m <= 12) {
        return d + ' ' + MONTHS_SHORT[m - 1] + ' ' + y;
      }
    }

    var parsed = Date.parse(s);
    if (!isNaN(parsed)) {
      var dt = new Date(parsed);
      return dt.getDate() + ' ' + MONTHS_SHORT[dt.getMonth()] + ' ' + dt.getFullYear();
    }

    return s;
  }

  function parseReview(revEl) {
    if (!revEl) return null;
    var score =
      revEl.getAttribute('data-score') ||
      (revEl.querySelector('[data-score]') && revEl.querySelector('[data-score]').getAttribute('data-score')) ||
      '';
    var ratingEl = revEl.querySelector('.jdgm-rev__rating');
    if (ratingEl) {
      var aria = ratingEl.getAttribute('aria-label') || '';
      // Prefer "X out of 5" / "Rated X" so we don't grab the trailing "5"
      var match =
        aria.match(/(\d+(?:\.\d+)?)\s*(?:out of|\/)\s*5/i) ||
        aria.match(/rated?\s*(\d+(?:\.\d+)?)/i) ||
        aria.match(/(\d+(?:\.\d+)?)/);
      if (match) score = match[1];
      if (!score) {
        var onStars = ratingEl.querySelectorAll(
          '.jdgm-star--on, .jdgm-star.jdgm--on, [class*="star"][class*="on"]'
        ).length;
        if (onStars > 0) score = onStars;
      }
    }
    if (!score) score = 5;
    var titleEl = revEl.querySelector('.jdgm-rev__title');
    var bodyEl = revEl.querySelector('.jdgm-rev__body');
    var helpfulEl =
      revEl.querySelector('.jdgm-rev__votes') ||
      revEl.querySelector('.jdgm-rev__helpful-vote') ||
      revEl.querySelector('[class*="helpful"]');
    var body = (bodyEl && (bodyEl.textContent || bodyEl.innerText) || '').trim();
    var title = (titleEl && titleEl.textContent || '').trim();
    if (!body && !title) return null;
    var tsEl = revEl.querySelector('.jdgm-rev__timestamp');
    var dateRaw = tsEl
      ? (tsEl.getAttribute('data-content') || tsEl.getAttribute('datetime') || tsEl.textContent || '').trim()
      : '';
    return {
      score: Math.round(Number(score) || 5),
      stars: starsHtml(score),
      date: formatReviewDate(dateRaw),
      title: title,
      body: body,
      author: (
        revEl.querySelector('.jdgm-rev__author') && revEl.querySelector('.jdgm-rev__author').textContent
      )
        ? revEl.querySelector('.jdgm-rev__author').textContent.trim()
        : 'Customer',
      verified: !!revEl.querySelector('.jdgm-rev__buyer-badge'),
      helpful: formatHelpful((helpfulEl && helpfulEl.textContent) || ''),
    };
  }

  function renderPreviewCard(review) {
    var verified = review.verified ? ' <span class="verified">Verified</span>' : '';
    // Preview: one short message only (CSS clamps to 2 lines)
    var message = (review.body || review.title || '').trim();
    if (review.body && review.title && review.body.indexOf(review.title) !== 0) {
      message = (review.title + '. ' + review.body).trim();
    }
    return (
      '<article class="rev-card" role="listitem">' +
      '<div class="rev-row">' +
      '<span class="stars" aria-hidden="true">' +
      review.stars +
      '</span>' +
      (review.date ? '<span class="date">' + esc(review.date) + '</span>' : '') +
      '</div>' +
      (message ? '<p>' + esc(message) + '</p>' : '') +
      '<div class="rev-foot">' +
      '<span class="rev-author"><b>' +
      esc(review.author) +
      '</b>' +
      verified +
      '</span>' +
      '</div>' +
      '</article>'
    );
  }

  function renderThreadCard(review) {
    var verified = review.verified ? ' <span class="verified">Verified</span>' : '';
    var helpful = review.helpful ? '<span class="helpful">' + esc(review.helpful) + '</span>' : '';
    return (
      '<article class="rev-thread-card" data-rating="' +
      review.score +
      '" role="listitem">' +
      '<div class="rev-row">' +
      '<span class="stars" aria-hidden="true">' +
      review.stars +
      '</span>' +
      (review.date ? '<span class="date">' + esc(review.date) + '</span>' : '') +
      '</div>' +
      (review.title ? '<h4>' + esc(review.title) + '</h4>' : '') +
      (review.body ? '<p>' + esc(review.body) + '</p>' : '') +
      '<div class="rev-foot">' +
      '<span class="rev-author"><b>' +
      esc(review.author) +
      '</b>' +
      verified +
      '</span>' +
      helpful +
      '</div>' +
      '</article>'
    );
  }

  function getJdgmRoot() {
    return getJdgmRootFallback();
  }

  function collectReviews() {
    var root = getJdgmRoot();
    if (!root) return [];
    var revEls = root.querySelectorAll('.jdgm-rev:not([data-codeskin-rev-clone])');
    var reviews = [];
    revEls.forEach(function (revEl) {
      var parsed = parseReview(revEl);
      if (parsed) reviews.push(parsed);
    });
    return reviews;
  }

  function parseTotalCount() {
    var root = getJdgmRoot();
    if (!root) return 0;
    var countEl =
      root.querySelector('.jdgm-rev-widg__reviews-count') ||
      root.querySelector('[data-number-of-reviews]') ||
      root.querySelector('.jdgm-rev-widg__summary-text');
    if (!countEl) return 0;
    var raw = countEl.getAttribute('data-number-of-reviews') || countEl.textContent || '';
    var match = raw.replace(/,/g, '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  function parseAverageRating(reviews) {
    var root = getJdgmRoot();
    if (root) {
      var avgEl = root.querySelector('.jdgm-rev-widg__summary-average, [data-average-rating]');
      if (avgEl) {
        var fromAttr = avgEl.getAttribute('data-average-rating');
        if (fromAttr) return Math.round(parseFloat(fromAttr) * 10) / 10;
        var m = (avgEl.textContent || '').match(/(\d+(?:\.\d+)?)/);
        if (m) return Math.round(parseFloat(m[1]) * 10) / 10;
      }
    }
    if (!reviews.length) return null;
    var sum = reviews.reduce(function (acc, r) {
      return acc + (Number(r.score) || 0);
    }, 0);
    return Math.round((sum / reviews.length) * 10) / 10;
  }

  function parseHistogram(reviews) {
    var root = getJdgmRoot();
    var buckets = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    var found = false;

    if (root) {
      root.querySelectorAll('.jdgm-histogram__row').forEach(function (row) {
        var rating = row.getAttribute('data-rating') || row.getAttribute('data-star') || '';
        if (!rating) {
          var label = row.querySelector('.jdgm-histogram__star, .jdgm-histogram__label');
          if (label) rating = (label.textContent || '').replace(/\D/g, '');
        }
        rating = parseInt(rating, 10);
        if (!rating) return;
        found = true;
        var freq = row.querySelector('.jdgm-histogram__frequency, .jdgm-histogram__count');
        var freqText = freq ? freq.textContent : '';
        var countMatch = freqText.replace(/,/g, '').match(/(\d+)/g);
        if (countMatch && countMatch.length) {
          buckets[rating] = parseInt(countMatch[countMatch.length - 1], 10);
        }
      });
    }

    if (!found) {
      reviews.forEach(function (r) {
        var star = Math.max(1, Math.min(5, Math.round(Number(r.score) || 5)));
        buckets[star] = (buckets[star] || 0) + 1;
      });
    }

    var bucketTotal = [5, 4, 3, 2, 1].reduce(function (sum, star) {
      return sum + (buckets[star] || 0);
    }, 0);
    var total = parseTotalCount() || bucketTotal || reviews.length || 1;
    var percents = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    [5, 4, 3, 2, 1].forEach(function (star) {
      percents[star] = total > 0 ? Math.round(((buckets[star] || 0) / total) * 1000) / 10 : 0;
    });

    return { buckets: buckets, percents: percents };
  }

  function initialsFromName(name) {
    return (name || '')
      .split(/\s+/)
      .filter(Boolean)
      .map(function (w) {
        return w.charAt(0);
      })
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  function renderLandingMarquee(track, reviews, force) {
    if (!track || !reviews.length) return;
    var root = track.closest('[data-codeskin-reviews-root]');
    reviews = reviewsForMarquee(reviews, root);
    if (!reviews.length) return;
    if (track.dataset.marqueeBound === 'true' && !force) return;
    var tones = ['a', 'b', 'c', 'd', 'e'];
    var max = Math.min(reviews.length, 28);

    function chipHTML(r, i) {
      var initials = initialsFromName(r.author) || 'CS';
      var tone = tones[i % tones.length];
      var text = (r.body || r.title || '').trim();
      if (text.length > 180) text = text.slice(0, 177) + '…';
      return (
        '<article class="pq-chip">' +
        '<figure class="pq-chip-portrait tone-' +
        tone +
        '"><span class="pq-portrait-initials">' +
        esc(initials) +
        '</span></figure>' +
        '<div class="pq-chip-body">' +
        '<div class="pq-chip-stars" aria-label="' +
        r.score +
        ' out of 5 stars">' +
        starsHtml(r.score) +
        '</div>' +
        '<p class="pq-chip-text">' +
        esc(text) +
        '</p>' +
        '<div class="pq-chip-attr">' +
        '<div class="pq-name">' +
        esc(r.author) +
        '</div>' +
        '<div class="pq-role">' +
        (r.verified ? 'Verified buyer' : 'CodeSkin customer') +
        '</div>' +
        '</div>' +
        '</div>' +
        '</article>'
      );
    }

    var slice = [];
    for (var i = 0; i < max; i++) slice.push(reviews[i]);
    var html = slice.map(chipHTML).join('');
    track.innerHTML = html + html;
    track.dataset.marqueeBound = 'true';
  }

  function updateLandingStats(root, total, avg) {
    if (!root) return;
    var avgEl = root.querySelector('[data-codeskin-rev-avg-num]');
    var avgLbl = root.querySelector('[data-codeskin-rev-avg-label]');
    var openBtn = root.querySelector('[data-codeskin-open-reviews]');

    if (total > 0) {
      var totalStr = total.toLocaleString();
      if (avgLbl) avgLbl.textContent = 'avg. rating across ' + totalStr + '+ verified reviews';
      if (openBtn && !openBtn.getAttribute('data-count-set')) {
        openBtn.innerHTML =
          'Read all ' +
          totalStr +
          ' reviews <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
        openBtn.setAttribute('data-count-set', 'true');
      }
      var dialogCount = document.querySelector('#rev-dialog .rev-dialog-count');
      if (dialogCount) dialogCount.textContent = '(' + totalStr + ')';
      var dialogTotal = document.querySelector('[data-codeskin-rev-total]');
      if (dialogTotal) dialogTotal.textContent = totalStr;
    }
    if (avg != null && avgEl) {
      avgEl.innerHTML = String(avg) + '<span class="den">/5</span>';
    }

    var dialogAvg = document.querySelector('[data-codeskin-rev-dialog-avg]');
    if (dialogAvg && avg != null) {
      dialogAvg.innerHTML = String(avg) + ' <span class="stars" aria-hidden="true">★★★★★</span>';
    }
    var dialogCountEl = document.querySelector('[data-codeskin-rev-dialog-count]');
    if (dialogCountEl && total > 0) {
      dialogCountEl.textContent = '(' + total.toLocaleString() + ')';
    }
  }

  function markPreviewSynced(preview) {
    if (preview) preview.classList.add('has-custom-reviews');
  }

  function reloadJudgeMeWidgets() {
    if (window.jdgmCacheServer && typeof window.jdgmCacheServer.reload === 'function') {
      window.jdgmCacheServer.reload();
    } else if (window.jdgm && typeof window.jdgm.loadWidgets === 'function') {
      window.jdgm.loadWidgets();
    }
  }

  function mountReviewWidget() {
    if (widgetPool) return widgetPool;

    var pool = document.querySelector('[data-codeskin-judgeme-widget].codeskin-rev-app-pool');
    if (!pool) return null;

    pool.removeAttribute('hidden');
    pool.style.removeProperty('display');
    pool.style.removeProperty('visibility');
    widgetPool = pool;
    reloadJudgeMeWidgets();
    return pool;
  }

  function getJdgmRootFallback() {
    if (widgetPool) return widgetPool;
    return (
      document.querySelector('.codeskin-rev-app-pool .jdgm-rev-widg') ||
      document.querySelector('.jdgm-rev-widg')
    );
  }

  function runGlobalSync() {
    reviewControllers.forEach(function (ctrl) {
      if (ctrl && typeof ctrl.syncAll === 'function') ctrl.syncAll();
    });
  }

  function runGlobalApplyFilter() {
    for (var i = reviewControllers.length - 1; i >= 0; i--) {
      if (reviewControllers[i] && typeof reviewControllers[i].applyFilter === 'function') {
        reviewControllers[i].applyFilter();
        return;
      }
    }
  }

  function wireDialogOnce() {
    if (dialogWired) return;
    var dialog = document.getElementById('rev-dialog');
    if (!dialog) return;

    dialogWired = true;
    var suppressBackdropClose = false;

    if (dialog.parentElement !== document.body) {
      document.body.appendChild(dialog);
    }

    function openDialog() {
      runGlobalSync();
      suppressBackdropClose = true;
      try {
        if (typeof dialog.showModal === 'function') {
          if (!dialog.open) dialog.showModal();
        } else {
          dialog.setAttribute('open', 'open');
        }
      } catch (err) {
        dialog.setAttribute('open', 'open');
      }
      document.documentElement.classList.add('rev-open');
      document.body.classList.add('rev-open');
      runGlobalApplyFilter();
      window.setTimeout(function () {
        suppressBackdropClose = false;
      }, 300);
    }

    function closeDialog() {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      document.documentElement.classList.remove('rev-open');
      document.body.classList.remove('rev-open');
    }

    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('[data-codeskin-open-reviews]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openDialog();
      },
      true
    );

    var closeBtn = document.querySelector('[data-codeskin-close-reviews]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeDialog();
      });
    }

    dialog.addEventListener('click', function (e) {
      if (suppressBackdropClose) return;
      if (e.target === dialog) closeDialog();
    });
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeDialog();
    });
  }

  function findJdgmNextControl(pool) {
    if (!pool) return null;
    return (
      pool.querySelector('.jdgm-paginate__next-page:not(.jdgm-disabled)') ||
      pool.querySelector(
        '.jdgm-paginate__page.jdgm-paginate__page--active + .jdgm-paginate__page:not(.jdgm-disabled)'
      ) ||
      pool.querySelector('.jdgm-paginate a[rel="next"]:not(.jdgm-disabled)') ||
      pool.querySelector('.jdgm-rev-widg__load-more:not(.jdgm-disabled)') ||
      pool.querySelector('a.jdgm-paginate__load-more:not(.jdgm-disabled)') ||
      pool.querySelector('button.jdgm-btn--load-more:not(.jdgm-disabled):not([disabled])')
    );
  }

  function findJdgmStarFilter(pool, star) {
    if (!pool) return null;
    var s = String(star);
    return (
      pool.querySelector(
        '.jdgm-histogram__row[data-rating="' + s + '"], .jdgm-histogram__row[data-star="' + s + '"]'
      ) ||
      pool.querySelector('.jdgm-histogram__row[data-rating="' + s + '"] .jdgm-histogram__bar') ||
      pool.querySelector(
        '.jdgm-histogram__row[data-rating="' + s + '"] a, .jdgm-histogram__row[data-rating="' + s + '"] button'
      ) ||
      pool.querySelector('a[data-rating="' + s + '"], button[data-rating="' + s + '"]')
    );
  }

  function findJdgmActiveStarFilter(pool) {
    if (!pool) return null;
    return (
      pool.querySelector(
        '.jdgm-histogram__row.jdgm-histogram__row--active, .jdgm-histogram__row.is-active, .jdgm-histogram__row[aria-pressed="true"], .jdgm-histogram__row.jdgm--on'
      ) || null
    );
  }

  function clearJdgmStarFilter(pool) {
    var active = findJdgmActiveStarFilter(pool);
    if (!active) return false;
    try {
      active.click();
      return true;
    } catch (err) {
      return false;
    }
  }

  function createController(root) {
    var revGrid = root.querySelector('[data-codeskin-rev-grid]');
    var revDist = document.querySelector('[data-codeskin-rev-dist]');
    var revFilters = document.querySelector('[data-codeskin-rev-filters]');
    var revThreadList = document.querySelector('[data-codeskin-rev-thread-list]');
    var revEmpty = document.querySelector('[data-codeskin-rev-empty]');
    var revShown = document.querySelector('[data-codeskin-rev-shown]');
    var revTotal = document.querySelector('[data-codeskin-rev-total]');
    var loadMoreBtn = document.querySelector('[data-codeskin-rev-load-more]');
    var preview = root.querySelector('[data-codeskin-rev-preview]');
    var marqueeTrack = root.querySelector('[data-codeskin-landing-marquee]');

    var previewLimit = parseInt(root.getAttribute('data-preview-limit') || '3', 10);
    if (isNaN(previewLimit)) previewLimit = 3;

    var syncAttempts = 0;
    var maxSyncAttempts = 80;
    var activeFilter = 'all';
    var allReviews = [];
    var reviewVault = [];
    var vaultKeys = {};
    // Persist 5★ reviews across Judge.me page swaps so the PDP strip can fill 3 cards
    var fiveStarPreviewPool = [];
    var previewFillClicks = 0;
    var maxPreviewFillClicks = 12;
    var previewFillTimer = null;
    var filterFetchTimer = null;
    var filterFetchClicks = 0;
    var maxFilterFetchClicks = 8;
    var lastHist = null;
    var jdgmFilterStar = null;
    var lastPreviewKey = null;

    function reviewKey(r) {
      return ((r.author || '') + '|' + (r.date || '') + '|' + (r.title || '') + '|' + (r.body || '')).slice(
        0,
        240
      );
    }

    function mergeIntoVault(reviews) {
      (reviews || []).forEach(function (r) {
        var key = reviewKey(r);
        if (vaultKeys[key]) return;
        vaultKeys[key] = true;
        var copy = {
          score: r.score,
          stars: r.stars,
          date: r.date,
          title: r.title,
          body: r.body,
          author: r.author,
          verified: r.verified,
          helpful: r.helpful,
          _key: key,
        };
        reviewVault.push(copy);
      });
      allReviews = reviewVault.slice();
    }

    function mergeFiveStarPreview(reviews) {
      (reviews || []).forEach(function (r) {
        if (Number(r.score) < 5) return;
        var key = reviewKey(r);
        var exists = fiveStarPreviewPool.some(function (x) {
          return x._key === key;
        });
        if (exists) return;
        fiveStarPreviewPool.push({
          score: r.score,
          stars: r.stars,
          date: r.date,
          title: r.title,
          body: r.body,
          author: r.author,
          verified: r.verified,
          helpful: r.helpful,
          _key: key,
        });
      });
    }

    function getFilteredReviews() {
      if (activeFilter === 'all') return allReviews.slice();
      return allReviews.filter(function (r) {
        return String(r.score) === String(activeFilter);
      });
    }

    function requestMoreFiveStarsForPreview() {
      if (!revGrid || fiveStarPreviewPool.length >= previewLimit) return;
      if (previewFillClicks >= maxPreviewFillClicks) return;
      var pool = getJdgmRoot();
      if (!pool) return;

      // Only paginate — never click Judge.me star filters here (that would hide 3★/4★ reviews).
      var control = findJdgmNextControl(pool);
      if (!control) return;

      previewFillClicks += 1;
      try {
        control.click();
      } catch (err) {
        /* ignore */
      }
      if (previewFillTimer) window.clearTimeout(previewFillTimer);
      previewFillTimer = window.setTimeout(function () {
        syncAll();
      }, 700);
    }

    function requestReviewsForActiveFilter() {
      if (activeFilter === 'all') return;
      if (filterFetchClicks >= maxFilterFetchClicks) return;
      var pool = getJdgmRoot();
      if (!pool) return;

      var matching = getFilteredReviews();
      var expected = lastHist && lastHist.buckets ? lastHist.buckets[activeFilter] || 0 : 0;
      if (matching.length > 0 && (expected === 0 || matching.length >= Math.min(expected, 5))) {
        return;
      }
      if (expected === 0 && matching.length === 0) return;

      var next = findJdgmNextControl(pool);
      var starControl = findJdgmStarFilter(pool, activeFilter);
      var control = null;

      if (jdgmFilterStar === String(activeFilter) && next) {
        control = next;
      } else if (starControl) {
        control = starControl;
        jdgmFilterStar = String(activeFilter);
      } else if (next) {
        control = next;
      }

      if (!control) return;

      filterFetchClicks += 1;
      try {
        control.click();
      } catch (err) {
        /* ignore */
      }
      if (filterFetchTimer) window.clearTimeout(filterFetchTimer);
      filterFetchTimer = window.setTimeout(function () {
        syncAll();
      }, 750);
    }

    function renderDistribution(hist) {
      if (!revDist || !hist) {
        if (revDist) revDist.hidden = true;
        return;
      }
      var html = '';
      [5, 4, 3, 2, 1].forEach(function (star) {
        var pct = hist.percents[star] || 0;
        var count = hist.buckets[star] || 0;
        html +=
          '<div class="rev-dist-row" data-row="' +
          star +
          '">' +
          '<span class="rev-dist-label">' +
          star +
          '★</span>' +
          '<div class="rev-dist-track"><div class="rev-dist-fill" style="width:' +
          pct +
          '%"></div></div>' +
          '<span class="rev-dist-pct">' +
          count.toLocaleString() +
          '</span>' +
          '</div>';
      });
      revDist.innerHTML = html;
      revDist.hidden = false;
    }

    function renderFilters(hist) {
      if (!revFilters) return;
      var total = parseTotalCount() || allReviews.length;
      var buckets = hist ? hist.buckets : {};
      var pills = [{ key: 'all', label: 'All', count: total }];
      [5, 4, 3, 2, 1].forEach(function (star) {
        pills.push({ key: String(star), label: star + '★', count: buckets[star] || 0 });
      });

      revFilters.innerHTML = pills
        .map(function (p) {
          var active = p.key === activeFilter ? ' active' : '';
          var selected = p.key === activeFilter ? 'true' : 'false';
          return (
            '<button class="rev-pill' +
            active +
            '" type="button" data-filter="' +
            p.key +
            '" role="tab" aria-selected="' +
            selected +
            '">' +
            p.label +
            ' <span class="n">' +
            p.count.toLocaleString() +
            '</span>' +
            '</button>'
          );
        })
        .join('');
      revFilters.hidden = false;

      revFilters.querySelectorAll('.rev-pill').forEach(function (pill) {
        pill.addEventListener('click', function () {
          revFilters.querySelectorAll('.rev-pill').forEach(function (x) {
            x.classList.remove('active');
            x.setAttribute('aria-selected', 'false');
          });
          pill.classList.add('active');
          pill.setAttribute('aria-selected', 'true');
          var nextFilter = pill.getAttribute('data-filter') || 'all';
          if (nextFilter !== activeFilter) {
            filterFetchClicks = 0;
          }
          activeFilter = nextFilter;

          if (activeFilter === 'all' && jdgmFilterStar) {
            clearJdgmStarFilter(getJdgmRoot());
            jdgmFilterStar = null;
          }

          applyFilter();
          if (
            activeFilter !== 'all' &&
            lastHist &&
            lastHist.buckets &&
            (lastHist.buckets[activeFilter] || 0) > 0
          ) {
            requestReviewsForActiveFilter();
          }
          updateLoadMore();
        });
      });
    }

    function applyFilter() {
      if (!revThreadList) return;
      var filtered = getFilteredReviews();
      revThreadList.innerHTML = filtered.map(renderThreadCard).join('');
      var shown = filtered.length;
      var expected =
        activeFilter === 'all'
          ? parseTotalCount() || allReviews.length
          : (lastHist && lastHist.buckets && lastHist.buckets[activeFilter]) || 0;

      if (revEmpty) {
        if (shown > 0) {
          revEmpty.hidden = true;
        } else {
          revEmpty.hidden = false;
          if (activeFilter !== 'all' && expected === 0) {
            revEmpty.textContent = 'No reviews for this rating.';
          } else {
            revEmpty.textContent = 'No reviews for this rating.';
          }
        }
      }
      if (revShown) revShown.textContent = String(shown);

      // Only try to fetch more when this star rating actually has reviews.
      if (shown === 0 && activeFilter !== 'all' && expected > 0) {
        requestReviewsForActiveFilter();
      }
    }

    function renderThread() {
      applyFilter();
    }

    function updateLoadMore() {
      if (!loadMoreBtn) return;
      var pool = getJdgmRoot();
      var filtered = getFilteredReviews();
      var expected =
        activeFilter === 'all'
          ? parseTotalCount() || allReviews.length
          : (lastHist && lastHist.buckets && lastHist.buckets[activeFilter]) || 0;

      // Zero reviews for this filter — hide load more, just show empty state.
      if (activeFilter !== 'all' && expected === 0) {
        loadMoreBtn.disabled = true;
        loadMoreBtn.hidden = true;
        return;
      }

      loadMoreBtn.hidden = false;
      if (!pool) {
        loadMoreBtn.disabled = true;
        return;
      }
      var next = findJdgmNextControl(pool);
      var starControl =
        activeFilter !== 'all' && filtered.length < expected
          ? findJdgmStarFilter(pool, activeFilter)
          : null;
      var moreLoaded = filtered.length < expected;
      loadMoreBtn.disabled = !(next || starControl || moreLoaded);
    }

    function syncAll() {
      var fresh = collectReviews();
      if (!fresh.length && !reviewVault.length) return false;

      mergeIntoVault(fresh);
      mergeFiveStarPreview(allReviews);

      if (revGrid && previewLimit > 0) {
        // Main PDP strip: prefer 5-star; fall back to best available so we never show zero.
        var previewReviews = fiveStarPreviewPool.slice(0, previewLimit);
        if (previewReviews.length < previewLimit) {
          allReviews.forEach(function (r) {
            if (previewReviews.length >= previewLimit) return;
            if (Number(r.score) >= 5) return;
            var key = reviewKey(r);
            var exists = previewReviews.some(function (x) {
              return x._key === key || reviewKey(x) === key;
            });
            if (exists) return;
            previewReviews.push(r);
          });
        }
        if (previewReviews.length) {
          var slice = previewReviews.slice(0, previewLimit);
          // Auto-pagination (requestMoreFiveStarsForPreview) can call syncAll() several
          // times while it fills in 5-star cards. Skip re-rendering when the set hasn't
          // actually changed so we don't repeatedly reflow/shove the page below the grid.
          var previewKey = slice.map(reviewKey).join('~');
          if (previewKey !== lastPreviewKey) {
            lastPreviewKey = previewKey;
            revGrid.innerHTML = slice.map(renderPreviewCard).join('');
          }
          markPreviewSynced(preview);
        }
        if (fiveStarPreviewPool.length < previewLimit) {
          requestMoreFiveStarsForPreview();
        }
      }

      if (marqueeTrack) {
        renderLandingMarquee(marqueeTrack, allReviews, true);
      }

      var total = parseTotalCount() || allReviews.length;
      var avg = parseAverageRating(allReviews);
      updateLandingStats(root, total, avg);

      var hist = parseHistogram(allReviews);
      lastHist = hist;
      renderDistribution(hist);
      renderFilters(hist);
      renderThread();

      if (revTotal && total) revTotal.textContent = total.toLocaleString();

      document.querySelectorAll('.rev-link[data-codeskin-open-reviews]').forEach(function (btn) {
        if (total > 0) btn.textContent = 'Read all ' + total.toLocaleString() + ' →';
      });

      updateLoadMore();
      if (
        activeFilter !== 'all' &&
        lastHist &&
        lastHist.buckets &&
        (lastHist.buckets[activeFilter] || 0) > 0
      ) {
        requestReviewsForActiveFilter();
      }

      document.dispatchEvent(
        new CustomEvent('codeskin:reviews-synced', {
          detail: { reviews: allReviews, total: total, average: avg },
        })
      );
      return true;
    }

    function trySyncLoop() {
      syncAttempts += 1;
      mountReviewWidget();
      if (syncAll()) return;
      if (syncAttempts < maxSyncAttempts) {
        window.setTimeout(trySyncLoop, 500);
      }
    }

    function boot() {
      mountReviewWidget();
      trySyncLoop();
      reviewControllers.push({ syncAll: syncAll, applyFilter: applyFilter });
      wireDialogOnce();
    }

    var observer = new MutationObserver(function () {
      if (syncAll()) updateLoadMore();
    });

    function observeJdgmRoot() {
      var pool = getJdgmRoot();
      if (pool) observer.observe(pool, { childList: true, subtree: true });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        var pool = getJdgmRoot();
        if (!pool) return;

        var next = findJdgmNextControl(pool);
        var starControl = activeFilter !== 'all' ? findJdgmStarFilter(pool, activeFilter) : null;
        var control = null;

        if (activeFilter !== 'all' && jdgmFilterStar !== String(activeFilter) && starControl) {
          control = starControl;
          jdgmFilterStar = String(activeFilter);
        } else if (next) {
          control = next;
        } else if (starControl) {
          control = starControl;
          jdgmFilterStar = String(activeFilter);
        }

        if (!control) return;
        try {
          control.click();
        } catch (err) {
          /* ignore */
        }
        window.setTimeout(function () {
          syncAll();
        }, 700);
      });
    }

    return {
      boot: boot,
      syncAll: syncAll,
      observeJdgmRoot: observeJdgmRoot,
      trySyncLoop: trySyncLoop,
    };
  }

  function initAll() {
    wireDialogOnce();
    document.querySelectorAll('[data-codeskin-reviews-root]').forEach(function (root) {
      if (root.dataset.reviewsInit === 'true') return;
      root.dataset.reviewsInit = 'true';
      var ctrl = createController(root);
      if (!ctrl) return;
      ctrl.boot();
      ctrl.observeJdgmRoot();
      window.addEventListener('load', function () {
        reloadJudgeMeWidgets();
        ctrl.trySyncLoop();
        ctrl.observeJdgmRoot();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.codeskinReviews = {
    reloadJudgeMeWidgets: reloadJudgeMeWidgets,
    formatReviewDate: formatReviewDate,
    renderLandingMarquee: renderLandingMarquee,
    mountReviewWidget: mountReviewWidget,
  };
})();
